import { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../../../../../../app/store';
import { updateSelectedFeeSchedule } from '../../../../../../../services/ContractService/contractSlice';
import { GrouperView } from '../../../../../../../services/ContractService/types/contractTypes';

interface GrouperFormData {
    code: string;
    rate: number;
    isPerDiem: boolean;
}

interface UseGrouperFormProps {
    editingGrouper: GrouperView | null;
    open: boolean;
    onClose: () => void;
}

interface UseGrouperFormResult {
    formData: GrouperFormData;
    formErrors: { [key: string]: string };
    handleFormChange: (field: keyof GrouperFormData, value: any) => void;
    handleSave: () => void;
    handleDelete: () => void;
}

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const initialFormData: GrouperFormData = {
    code: '',
    rate: 0,
    isPerDiem: false
};

export const useGrouperForm = ({
    editingGrouper,
    open,
    onClose
}: UseGrouperFormProps): UseGrouperFormResult => {
    const dispatch = useAppDispatch();
    const selectedFeeSchedule = useAppSelector((state) => state.contract.currentFeeSchedule);
    const groupers = selectedFeeSchedule?.groupers || [];

    const [formData, setFormData] = useState<GrouperFormData>(initialFormData);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (editingGrouper) {
            setFormData({
                code: editingGrouper.code,
                rate: editingGrouper.rate,
                isPerDiem: editingGrouper.isPerDiem
            });
        } else {
            setFormData(initialFormData);
        }
        setFormErrors({});
    }, [editingGrouper, open]);

    const handleFormChange = useCallback((field: keyof GrouperFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [formErrors]);

    const validateForm = useCallback((): boolean => {
        const errors: { [key: string]: string } = {};

        if (!formData.code.trim()) {
            errors.code = 'Code is required';
        } else if (groupers.some(g => g.code === formData.code.trim() && g.id !== editingGrouper?.id)) {
            errors.code = 'Code already exists';
        }

        if (formData.rate < 0) {
            errors.rate = 'Rate cannot be negative';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, groupers, editingGrouper]);

    const handleSave = useCallback(() => {
        if (!validateForm()) return;

        const trimmedCode = formData.code.trim();
        let updatedGroupers: GrouperView[];

        if (editingGrouper) {
            updatedGroupers = groupers.map(g =>
                g.id === editingGrouper.id
                    ? {
                        ...g,
                        code: trimmedCode,
                        rate: formData.rate,
                        isPerDiem: formData.isPerDiem
                    }
                    : g
            );
        } else {
            const newGrouper: GrouperView = {
                id: generateTempId(),
                code: trimmedCode,
                rate: formData.rate,
                isPerDiem: formData.isPerDiem
            };
            updatedGroupers = [...groupers, newGrouper];
        }

        dispatch(updateSelectedFeeSchedule({ groupers: updatedGroupers }));
        onClose();
    }, [validateForm, formData, editingGrouper, groupers, dispatch, onClose]);

    const handleDelete = useCallback(() => {
        if (editingGrouper && window.confirm('Are you sure you want to delete this grouper?')) {
            const updatedGroupers = groupers.filter(g => g.id !== editingGrouper.id);
            dispatch(updateSelectedFeeSchedule({ groupers: updatedGroupers }));
            onClose();
        }
    }, [editingGrouper, groupers, dispatch, onClose]);

    return {
        formData,
        formErrors,
        handleFormChange,
        handleSave,
        handleDelete
    };
};
